/**
 * Canonical browser identity — shared by the data builders (generate-worker) and the render
 * component (reporting-blocks' Browser Type card), so both agree on what a browser IS.
 *
 * The demographic feed does not agree with itself: production carries `chrome`, `Chrome`,
 * `Google Chrome`, `edge` / `Edge` / `MS Edge`, engines (`WebKit`, `Waterfox`, `GSA`) and a
 * long tail of in-app browsers (`Instagram`, `Facebook`, `Unknown`) across different rows.
 *
 * `normaliseBrowser` folds a raw name onto one of a fixed set of canonical keys; anything we
 * cannot name lands on `other`. `collapseBrowsers` then groups a breakdown by that key so the
 * card shows ONE row per browser — in particular one "Other" bucket, not a scatter of
 * globe-marked rows the card can't tell apart (each unnamed browser renders as the same generic
 * globe, so two of them read as "two Others").
 *
 * This is a pure module (no React) precisely so the worker can share it without pulling the
 * rendering package in. `reporting-blocks/browser-logos.tsx` re-imports `normaliseBrowser` from
 * here to keep a single source of truth for the mapping.
 */

/** Fold a raw browser name onto a canonical key. `other` is the generic (globe) bucket. */
export function normaliseBrowser(name: string): string {
  const n = String(name ?? '').toLowerCase();
  if (n.includes('edge')) return 'edge';                                   // before chrome: "Edge" is Chromium
  if (n.includes('samsung')) return 'samsung';
  if (n.includes('brave')) return 'brave';
  if (n.includes('opera') || n.includes('opr')) return 'opera';
  if (n.includes('chrome') || n.includes('chromium')) return 'chrome';
  if (n.includes('firefox') || n.includes('mozilla') || n.includes('waterfox')) return 'firefox';
  if (n.includes('safari') || n.includes('webkit')) return 'safari';       // WebKit UAs are Safari-family
  if (n.includes('gsa') || n === 'google' || n.includes('google app')) return 'google';
  if (n.includes('explorer') || n === 'ie' || n.includes('msie') || n === 'trident') return 'ie';
  return 'other';
}

/** The display label for each canonical key — must match the labels on the rendered logo marks. */
export const CANONICAL_BROWSER_LABEL: Record<string, string> = {
  chrome: 'Chrome',
  safari: 'Safari',
  firefox: 'Firefox',
  edge: 'Edge',
  opera: 'Opera',
  brave: 'Brave',
  samsung: 'Samsung Internet',
  google: 'Google',
  ie: 'Internet Explorer',
  other: 'Other',
};

/** The canonical display label for a raw browser name. */
export function canonicalBrowserLabel(name: string): string {
  return CANONICAL_BROWSER_LABEL[normaliseBrowser(name)] ?? 'Other';
}

/**
 * Collapse a browser breakdown onto its canonical identities: rows with the same canonical key
 * (e.g. `Chrome` + `Google Chrome`, or two unnamed in-app browsers) are summed into a single row
 * carrying the canonical label. Zero-click rows are dropped and the result is sorted
 * clicks-descending — the order the card presents. Sum is preserved, so section totals and the
 * click reconciliation are unaffected.
 */
export function collapseBrowsers(
  rows: ReadonlyArray<{ name?: unknown; clicks?: unknown }> | null | undefined,
): Array<{ name: string; clicks: number }> {
  const by = new Map<string, number>();
  for (const r of rows ?? []) {
    const key = normaliseBrowser(String(r?.name ?? ''));
    const clicks = Number(r?.clicks);
    by.set(key, (by.get(key) ?? 0) + (Number.isFinite(clicks) ? clicks : 0));
  }
  return [...by.entries()]
    .filter(([, clicks]) => clicks > 0)
    .map(([key, clicks]) => ({ name: CANONICAL_BROWSER_LABEL[key] ?? 'Other', clicks }))
    .sort((a, b) => b.clicks - a.clicks);
}
